import React from 'react'
import { useNavigate } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { makeStyles } from '@mui/styles'
import getMaterialsCell from 'components/generic/datagrid/cells/MaterialsCell'
import { formatNumber, setDecimalPrecision } from 'utils/helpers'
import { dateTimeFormatter } from 'utils/data-manipulation/valueFormatter'

import DataGridWithSearch from './DataGridWithSearch'

const useStyles = makeStyles(theme => ({
  root: {
    '& .MuiDataGrid-columnHeaderTitleContainer': {
      padding: theme.spacing(0),
    },
    '& .MuiDataGrid-columnHeaderTitle': {
      paddingLeft: theme.spacing(0.75),
      paddingRight: theme.spacing(0),
    },
    '& .MuiDataGrid-row': {
      cursor: 'pointer',
    },
    '& .MuiDataGrid-columnHeader:focus': {
      outline: 'none',
    },
    '& .MuiDataGrid-cell:focus': {
      outline: 'none',
    },
    '& .MuiDataGrid-cell:focus-within': {
      outline: 'none',
    },
  },
}))

const getColumns = (timezone, enableDisplayRegradeInformation) => {
  const columns = [
    {
      field: 'displayId',
      headerName: 'Regrade #',
      flex: 1,
    },
    {
      field: 'updatedAt',
      headerName: 'Date Last Updated',
      flex: 1,
      renderCell: params => {
        const { updatedAt } = params?.row || {}
        if (updatedAt) {
          return dateTimeFormatter(updatedAt, { timeZone: timezone })
        } else {
          return '--'
        }
      },
      // This method ensures that sorting will work correctly regardless of the date format displayed to the user.
      sortComparator: (param1, param2) => {
        const timestamp1 = param1 ? param1.getTime() : 0
        const timestamp2 = param2 ? param2.getTime() : 0
        return timestamp1 - timestamp2
      },
    },
    {
      field: 'effectiveDate',
      headerName: 'Effective Date',
      flex: 1,
      renderCell: params => {
        const { effectiveDate } = params?.row || {}
        if (effectiveDate) {
          return dateTimeFormatter(effectiveDate, { timeZone: timezone })
        } else {
          return '--'
        }
      },
      // This method ensures that sorting will work correctly regardless of the date format displayed to the user.
      sortComparator: (param1, param2) => {
        const timestamp1 = param1 ? param1.getTime() : 0
        const timestamp2 = param2 ? param2.getTime() : 0

        return timestamp1 - timestamp2
      },
    },
    {
      field: 'fromMaterial',
      headerName: 'From',
      flex: 1,
      sortable: false,
    },
    {
      field: 'fromQuantity',
      headerName: 'Quantity',
      headerAlign: 'right',
      align: 'right',
      renderCell: params => formatNumber(`${params?.row?.fromQuantity}`),
      flex: 1,
    },
    {
      field: 'toMaterials',
      headerName: 'To',
      flex: 1,
      sortable: false,
      renderCell: params => getMaterialsCell(params, 'toMaterials'),
    },
    {
      field: 'toQuantity',
      headerName: 'Quantity',
      headerAlign: 'right',
      align: 'right',
      renderCell: params => formatNumber(`${params?.row?.toQuantity}`),
      flex: 1,
    },
    {
      field: 'costTransferred',
      headerName: 'Cost Transferred',
      headerAlign: 'right',
      align: 'right',
      renderCell: ({ row }) => `$${setDecimalPrecision(row?.costTransferred, { decimalPrecisionOverride: 2 })}`,
      flex: 1,
    },
    {
      field: 'valueTransferred',
      headerName: 'Value Transferred',
      headerAlign: 'right',
      align: 'right',
      renderCell: ({ row }) => `$${setDecimalPrecision(row?.valueTransferred, { decimalPrecisionOverride: 2 })}`,
      flex: 1,
    },
  ]
  const filteredColumns = columns.filter(column => {
    if (column.field === 'valueTransferred' && enableDisplayRegradeInformation) return false
    if (column.field === 'costTransferred' && !enableDisplayRegradeInformation) return false
    return true
  })
  return filteredColumns
}

const RegradesListGrid = props => {
  const navigate = useNavigate()
  const classes = useStyles()

  const { enableDisplayRegradeInformation } = useSelector(state => state.featureFlags?.variables || {})
  const { defaultYardTimezone } = useSelector(state => state.auth)

  const timezone = defaultYardTimezone ?? 'UTC'

  const columns = getColumns(timezone, enableDisplayRegradeInformation)

  return (
    <DataGridWithSearch
      {...props}
      className={classes.root}
      columns={columns}
      onRowClick={params => {
        navigate(params.row.id)
      }}
    />
  )
}

RegradesListGrid.propTypes = DataGridWithSearch.propTypes

RegradesListGrid.defaultProps = DataGridWithSearch.defaultProps

export default RegradesListGrid
